/**
 * LooksNotificationService
 *
 * Détecte quand de nouveaux looks sont générés (grâce à un nouvel article
 * ajouté au dressing OU à une mise à jour du catalogue) et pousse une
 * notification locale pour prévenir l'utilisateur.
 *
 * Stratégie :
 *  - On sauvegarde une "signature" des derniers looks connus par user
 *    (ensemble des hash des items composant chaque tenue)
 *  - À chaque nouvelle génération, on compare la signature actuelle à la signature précédente
 *  - Si de nouveaux looks apparaissent → on crée une notification locale
 *  - La notif est stockée dans AsyncStorage et lue par NotificationsScreen
 *
 * Push local via expo-notifications si disponible, sinon juste stockage in-app.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getCurrentLang } from '../i18n';

/**
 * Textes localisés pour notifications "nouveaux looks".
 * Utilisé hors contexte React (services natifs).
 */
function getLooksNotifTexts(lang, count) {
  const isEN = lang === 'en';
  return {
    titleSingle: isEN ? 'New look available' : 'Nouveau look disponible !',
    titleMulti:  isEN ? `${count} new looks generated` : `${count} nouveaux looks générés !`,
    reasonCatalog:  isEN ? 'New items available in the catalog' : 'Nouveaux articles disponibles dans le catalogue',
    reasonWardrobe: isEN ? 'New items added to your wardrobe' : 'Nouveaux articles ajoutés à votre garde-robe',
    bodySuffix: isEN ? ' — fresh AI outfits have been created.' : ' — de nouvelles tenues IA ont été créées pour vous.',
  };
}

// Clés de storage
const keySignature = (uid) => `outfit_looks_sig_${uid || 'guest'}`;
const keyNotifs    = (uid) => `outfit_notifications_${uid || 'guest'}`;

// Fenêtre anti-spam : on ne génère pas plus d'une notif "new looks" par heure
const MIN_INTERVAL_MS = 60 * 60 * 1000;
const keyLastNotif = (uid) => `outfit_last_looks_notif_${uid || 'guest'}`;

/**
 * Calcule une signature stable d'une liste de looks.
 * Chaque look est hashé par la concaténation des ids de ses items triés.
 */
function buildLooksSignature(outfits) {
  if (!Array.isArray(outfits) || !outfits.length) return new Set();
  const sigs = outfits.map(o => {
    const ids = (o.items || [])
      .map(it => String(it.id || it.partner_sku || it.name || ''))
      .sort();
    return ids.join('|');
  });
  return new Set(sigs);
}

/**
 * Sauvegarde la signature actuelle des looks.
 */
export async function saveLooksSignature(userId, outfits) {
  try {
    const sig = buildLooksSignature(outfits);
    await AsyncStorage.setItem(keySignature(userId), JSON.stringify(Array.from(sig)));
  } catch {}
}

/**
 * Récupère la signature précédente.
 */
async function loadLooksSignature(userId) {
  try {
    const raw = await AsyncStorage.getItem(keySignature(userId));
    if (!raw) return null;
    return new Set(JSON.parse(raw));
  } catch { return null; }
}

/**
 * Compare nouvelle liste vs précédente. Retourne le nombre de nouveaux looks détectés.
 */
export async function detectNewLooks(userId, outfits) {
  const prev = await loadLooksSignature(userId);
  // Premier passage : on save mais on ne notifie pas (pas de baseline)
  if (!prev) {
    await saveLooksSignature(userId, outfits);
    return { isFirstRun: true, newLooks: 0 };
  }
  const current = buildLooksSignature(outfits);
  let newCount = 0;
  for (const sig of current) {
    if (!prev.has(sig)) newCount++;
  }
  return { isFirstRun: false, newLooks: newCount, totalLooks: current.size };
}

/**
 * Ajoute une notification in-app au storage de l'utilisateur.
 */
export async function pushLooksNotification(userId, opts) {
  const { count = 1, reason = 'wardrobe' } = opts || {};
  try {
    // Anti-spam
    const lastRaw = await AsyncStorage.getItem(keyLastNotif(userId));
    const lastAt  = lastRaw ? parseInt(lastRaw, 10) : 0;
    if (Date.now() - lastAt < MIN_INTERVAL_MS) {
      console.log('[LooksNotif] skipped (too soon since last)');
      return false;
    }

    const raw = await AsyncStorage.getItem(keyNotifs(userId));
    const list = raw ? JSON.parse(raw) : [];
    const lang = (typeof getCurrentLang === 'function' ? getCurrentLang() : 'fr') || 'fr';
    const T = getLooksNotifTexts(lang, count);
    const reasonText = reason === 'catalog' ? T.reasonCatalog : T.reasonWardrobe;
    const notif = {
      id:        'look_' + Date.now(),
      type:      'new_looks',
      icon:      '✨',
      title:     count > 1 ? T.titleMulti : T.titleSingle,
      body:      reasonText + T.bodySuffix,
      createdAt: new Date().toISOString(),
      read:      false,
      deeplink:  'Suggestions',
    };
    list.unshift(notif);
    // Limite de 50 notifs max pour éviter que le storage gonfle
    const trimmed = list.slice(0, 50);
    await AsyncStorage.setItem(keyNotifs(userId), JSON.stringify(trimmed));
    await AsyncStorage.setItem(keyLastNotif(userId), String(Date.now()));
    console.log('[LooksNotif] pushed:', notif.title);

    // Bonus : push système si expo-notifications est disponible
    try {
      const Notifications = require('expo-notifications');
      if (Notifications && Notifications.scheduleNotificationAsync) {
        await Notifications.scheduleNotificationAsync({
          content: {
            title: notif.title,
            body:  notif.body,
            data:  { deeplink: notif.deeplink },
          },
          trigger: null, // immédiat
        });
      }
    } catch { /* expo-notifications non dispo en Expo Go web/sans dev build */ }

    return true;
  } catch(e) {
    console.warn('[LooksNotif] push failed:', e.message);
    return false;
  }
}

/**
 * Pipeline complet : à appeler après chaque nouvelle génération de looks.
 * Compare, notifie si nouveaux, sauvegarde la nouvelle signature.
 */
export async function checkAndNotifyNewLooks(userId, outfits, reason = 'wardrobe') {
  if (!userId || !Array.isArray(outfits)) return { newLooks: 0 };
  const res = await detectNewLooks(userId, outfits);
  if (!res.isFirstRun && res.newLooks > 0) {
    await pushLooksNotification(userId, { count: res.newLooks, reason });
  }
  await saveLooksSignature(userId, outfits);
  return res;
}

/**
 * Lister les notifs stockées.
 */
export async function getStoredNotifications(userId) {
  try {
    const raw = await AsyncStorage.getItem(keyNotifs(userId));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

/**
 * Marquer une notif comme lue.
 */
export async function markNotificationRead(userId, notifId) {
  try {
    const raw = await AsyncStorage.getItem(keyNotifs(userId));
    if (!raw) return;
    const list = JSON.parse(raw);
    const updated = list.map(n => n.id === notifId ? { ...n, read: true } : n);
    await AsyncStorage.setItem(keyNotifs(userId), JSON.stringify(updated));
  } catch {}
}

/**
 * Marquer toutes comme lues.
 */
export async function markAllRead(userId) {
  try {
    const raw = await AsyncStorage.getItem(keyNotifs(userId));
    if (!raw) return;
    const list = JSON.parse(raw);
    const updated = list.map(n => ({ ...n, read: true }));
    await AsyncStorage.setItem(keyNotifs(userId), JSON.stringify(updated));
  } catch {}
}

/**
 * Supprimer une notif.
 */
export async function deleteNotification(userId, notifId) {
  try {
    const raw = await AsyncStorage.getItem(keyNotifs(userId));
    if (!raw) return;
    const list = JSON.parse(raw);
    const filtered = list.filter(n => n.id !== notifId);
    await AsyncStorage.setItem(keyNotifs(userId), JSON.stringify(filtered));
  } catch {}
}
